import { Observable } from 'rxjs';
import { InjectionToken } from '../../injection';
import { EmptyResponse } from '../data/empty-response.do';
import { MobizClientPluginConfiguration } from '../data/mobiz-client-plugin-configuration.do';

import { MobizClientPluginInfo } from '../data/mobiz-client-plugin-info.do';
import { OrganizationMembership } from '../data/organization-membership.do';
import { PagedResults } from '../data/paged-results.model';
import { Dictionary, ParameterValueType } from '../interfaces/dictionary.interface';

export type ServiceConfigurationScopeFilter = 'MobizAdministrator' | 'Organization' | 'All';

/**
 * Possible query parameters for fetching Mobiz Client Plugin Configurations.
 *  @param page 1-Indexed page number.
 *  @param pageSize Page size.
 *  @param scopeFilter Optional service configuration scope.
 *  @param scopeId Optional service configuration scope id.
 */
export interface MobizClientPluginConfigurationQueryParameters extends Dictionary<ParameterValueType> {
  page?: number;
  pageSize?: number;
  scopeFilter?: ServiceConfigurationScopeFilter;
  scopeId?: string;
}

export interface IMobizExtensionService {
  /**
   * Returns all the available client plugins.
   */
  getAllClientPlugins(): Observable<MobizClientPluginInfo[]>;

  /**
   * Returns all the enabled client plugins.
   * @param configuration Optional Organization Membership. If null, returns the enabled plugins on the MobizAdministrator scope.
   */
  getEnabledClientPlugins(membership?: OrganizationMembership): Observable<MobizClientPluginInfo[]>;

  /**
   * Returns the URL for the plugin's bootstrap file.
   */
  getBootstrapFileUrl(plugin: MobizClientPluginInfo): string;

  /**
   * Creates a new Mobiz Client Plugin Configuration.
   * @param configuration The Mobiz Client Plugin Configuration to create.
   */
  createConfiguration(configuration: MobizClientPluginConfiguration): Observable<MobizClientPluginConfiguration>;

  /**
   * Retrieves the list of Mobiz Client Plugin Configurations.
   * @param queryParameters Dictionary containing the query parameters.
   */
  getAllConfigurations(queryParameters: MobizClientPluginConfigurationQueryParameters):
    Observable<PagedResults<MobizClientPluginConfiguration>>;

  /**
   * Retrieves a single Mobiz Client Plugin Configuration.
   * @param configurationId Configuration identifier.
   */
  getConfiguration(configurationId: string): Observable<MobizClientPluginConfiguration>;

  /**
   * Updates an existing Mobiz Client Plugin Configuration.
   * @param configuration The configuration to update.
   */
  updateConfiguration(configuration: MobizClientPluginConfiguration): Observable<MobizClientPluginConfiguration>;

  /**
   * Deletes an existing Mobiz Client Plugin Configuration.
   * @param configurationId The configuration identifier.
   */
  deleteConfiguration(configurationId: string): Observable<EmptyResponse>;
}
export const IMobizExtensionService = new InjectionToken('IMobizExtensionService');
